import { isDefined } from "@clipboard-health/util-ts";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import constate from "constate";
import { generatePath, useLocation } from "react-router-dom";

import { useGetVisit } from "../InHome/useGetVisitData";
import { useGetWorkplaceProfile } from "../Workplace/useGetWorkplaceProfile";
import { getVisitModalRouteParams } from "./getVisitModalRouteParams";

interface UseInHomeVisitModalsDataProps {
  baseNavigationPath: string;
}

function useInHomeVisitModalsData(props: UseInHomeVisitModalsDataProps) {
  const { baseNavigationPath } = props;
  const { pathname } = useLocation();
  const geoLocation = useWorkerGeoLocation();
  const modalRouteParams = getVisitModalRouteParams(baseNavigationPath, pathname);
  const visitId = modalRouteParams?.visitId;
  const caseId = modalRouteParams?.caseId;

  const {
    data,
    isLoading: isVisitLoading,
    isError: isVisitError,
  } = useGetVisit({
    visitId,
    caseId,
    geoLocation,
    includeOccurrences: true,
  });

  const visitData = data?.data;
  const caseData = data?.included?.find((item) => item.type === "case");
  const patientData = caseData?.attributes?.patient;
  const { data: workplaceData } = useGetWorkplaceProfile(
    {
      workplaceId: caseData?.attributes?.workplaceId ?? "",
    },
    {
      enabled: isDefined(caseData?.attributes?.workplaceId),
    }
  );

  const visitOccurrences = data?.included?.filter((item) => item.type === "visit-occurrence") ?? [];

  const generateModalPath = <S extends string>(
    path: S,
    params?: Parameters<typeof generatePath<S>>[1]
  ) => {
    return generatePath(
      `${baseNavigationPath}/${path}`,
      params as unknown as Parameters<typeof generatePath<`${string}/${S}`>>[1]
    );
  };

  return {
    baseNavigationPath,
    generateModalPath,
    visit: visitData,
    patient: patientData,
    case: caseData,
    workplaceName: workplaceData?.name ?? "",
    isVisitLoading,
    isVisitError,
    modalRouteParams,
    visitOccurrences,
  };
}

export const [InHomeVisitModalsDataProvider, useInHomeVisitModalsDataContext] =
  constate(useInHomeVisitModalsData);
